import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './style.module.scss';

export interface XYCursorPoint {
  x: number;
  y: number;
}

export interface XYCursorData {
  id: string;
  type: string;
  name: string;
  format?: string;
  snapX: number;
  snapY: number;
  delimiterChar?: string;
  graphId: string;
  previousAnswers?: XYCursorPoint[];
  ariaLabel?: string;
  ariaDescription?: string;
  tabAlign?: string;
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  maxPoints?: number;
}

interface XYCursorProps {
  data: XYCursorData;
  onPointsChange?: (points: XYCursorPoint[]) => void;
}

const XYCursor: React.FC<XYCursorProps> = ({ data }) => {
  const { t } = useTranslation();
  const {
    snapX = 1,
    snapY = 1,
    previousAnswers = [],
    ariaLabel,
    xMin = 0,
    xMax = 10,
    yMin = 0,
    yMax = 10,
  } = data;

  const [points] = useState<XYCursorPoint[]>(previousAnswers);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get theme colors from CSS variables
  const getThemeColor = (property: string): string => {
    if (typeof window !== 'undefined') {
      const value = window.getComputedStyle(document.documentElement).getPropertyValue(property).trim();
      if (value) return value;
    }
    // Fallback values if computed style is not available
    const fallbacks: Record<string, string> = {
      '--graph-grid': '#e0e0e0',
      '--graph-axis': '#333333',
      '--graph-text': '#666666',
      '--graph-point': '#3f51b5',
      '--graph-point-stroke': '#ffffff',
    };
    return fallbacks[property] || '';
  };

  const graphGridColor = getThemeColor('--graph-grid');
  const graphAxisColor = getThemeColor('--graph-axis');
  const graphTextColor = getThemeColor('--graph-text');
  const graphPointColor = getThemeColor('--graph-point');
  const graphPointStrokeColor = getThemeColor('--graph-point-stroke');
  
  const defaultAriaLabel = ariaLabel || t('XY_GRAPH');

  // Base dimensions (will scale responsively)
  const baseWidth = 400;
  const baseHeight = 400;
  const padding = 30;
  
  // Viewbox dimensions
  const viewBoxWidth = baseWidth + padding * 2;
  const viewBoxHeight = baseHeight + padding * 2;

  // Convert graph coordinates to SVG coordinates (within viewBox)
  // For negative ranges, center the origin at the middle of the graph
  const graphToSvg = useCallback(
    (point: XYCursorPoint) => {
      // Calculate the center position for the origin (0,0)
      const centerX = baseWidth / 2;
      const centerY = baseHeight / 2;
      
      // Calculate the scale factors
      const xRange = xMax - xMin;
      const yRange = yMax - yMin;
      const xScale = baseWidth / xRange;
      const yScale = baseHeight / yRange;
      
      // Convert coordinates with centered origin
      const x = centerX + (point.x * xScale);
      const y = centerY - (point.y * yScale); // Flip Y axis so positive Y goes up
      
      return { x, y };
    },
    [xMin, xMax, yMin, yMax]
  );



  // Generate grid lines for centered coordinate system
  const gridLines = [];
  const numXLines = Math.floor((xMax - xMin) / snapX) + 1;
  const numYLines = Math.floor((yMax - yMin) / snapY) + 1;

  // Generate vertical grid lines
  for (let i = 0; i < numXLines; i++) {
    const value = xMin + (i * snapX);
    const svgPos = graphToSvg({ x: value, y: 0 });
    gridLines.push(
      <line
        key={`v-${i}`}
        x1={svgPos.x}
        y1={0}
        x2={svgPos.x}
        y2={baseHeight}
        stroke={graphGridColor}
        strokeWidth="1"
      />
    );
  }

  // Generate horizontal grid lines
  for (let i = 0; i < numYLines; i++) {
    const value = yMin + (i * snapY);
    const svgPos = graphToSvg({ x: 0, y: value });
    gridLines.push(
      <line
        key={`h-${i}`}
        x1={0}
        y1={svgPos.y}
        x2={baseWidth}
        y2={svgPos.y}
        stroke={graphGridColor}
        strokeWidth="1"
      />
    );
  }

  return (
    <div className={styles.xyCursorWrapper} ref={containerRef}>
      <div 
        className={styles.xyCursorContainer} 
        role="img" 
        aria-label={defaultAriaLabel}
        aria-describedby={`xycursor-${data.graphId}-description`}
        data-graph-id={data.graphId}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
          className={styles.xyCursorSvg}
          preserveAspectRatio="xMidYMid meet"
        >
          <g transform={`translate(${padding}, ${padding})`}>
            {/* Grid */}
            <g className={styles.grid}>{gridLines}</g>

            {/* Axes - Centered coordinate system */}
            {/* X-axis (horizontal line through center) */}
            <line
              x1={0}
              y1={baseHeight / 2}
              x2={baseWidth}
              y2={baseHeight / 2}
              stroke={graphAxisColor}
              strokeWidth="2"
            />
            {/* Y-axis (vertical line through center) */}
            <line
              x1={baseWidth / 2}
              y1={0}
              x2={baseWidth / 2}
              y2={baseHeight}
              stroke={graphAxisColor}
              strokeWidth="2"
            />
            
            {/* Origin point (0,0) */}
            <circle
              cx={baseWidth / 2}
              cy={baseHeight / 2}
              r="3"
              fill={graphAxisColor}
            />

            {/* X-axis labels */}
            {Array.from({ length: Math.min(11, numXLines) }, (_, i) => {
              const step = Math.ceil(numXLines / 11);
              const index = i * step;
              if (index >= numXLines) return null;
              
              const value = xMin + (index * snapX);
              const svgPos = graphToSvg({ x: value, y: 0 });
              return (
                <text
                  key={`x-label-${i}`}
                  x={svgPos.x}
                  y={baseHeight / 2 + (value >= 0 ? 15 : -5)}
                  textAnchor="middle"
                  fontSize="10"
                  fill={graphTextColor}
                >
                  {value}
                </text>
              );
            })}

            {/* Y-axis labels */}
            {Array.from({ length: Math.min(11, numYLines) }, (_, i) => {
              const step = Math.ceil(numYLines / 11);
              const index = i * step;
              if (index >= numYLines) return null;
              
              const value = yMin + (index * snapY);
              const svgPos = graphToSvg({ x: 0, y: value });
              return (
                <text
                  key={`y-label-${i}`}
                  x={baseWidth / 2 + (value >= 0 ? -8 : 8)}
                  y={svgPos.y + 3}
                  textAnchor={value >= 0 ? "end" : "start"}
                  fontSize="10"
                  fill={graphTextColor}
                >
                  {value}
                </text>
              );
            })}

            {/* Points */}
            {points.map((point, index) => {
              const svgPos = graphToSvg(point);

              return (
                <g key={`point-${index}`}>
                  {/* Outer ring for better visibility */}
                  <circle
                    cx={svgPos.x}
                    cy={svgPos.y}
                    r="8"
                    fill="none"
                    stroke={graphPointColor}
                    strokeWidth="2"
                    opacity="0.1"
                    pointerEvents="none"
                  />
                  
                  {/* Main point circle */}
                  <circle
                    cx={svgPos.x}
                    cy={svgPos.y}
                    r="5"
                    fill={graphPointColor}
                    stroke={graphPointStrokeColor}
                    strokeWidth="2"
                    pointerEvents="none"
                  />
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default XYCursor;